import { observer } from "mobx-react-lite";
import { Button, Drawer } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import Step0TypeSelector from "./components/steps/StepTypeSelector";
import StepSelfEmployed from "./components/steps/StepSelfEmployed";
import Step1Requisites from "./components/steps/Step1Requisites";
import Step2Address from "./components/steps/Step2Address";
import Step3Director from "./components/steps/Step3Director";
import Step4Bank from "./components/steps/Step4Bank";
import LegalTabNav from "./components/steps/LegalTabNav";
import UnsavedStepModal from "./components/views/UnsavedStepModal";

/* ─── Main component ────────────────────────────────────────────────────── */

const LegalDataDrawer: React.FC = observer(() => {
  const { legal } = contractorDetailStore;
  const { isDrawerOpen, currentStep, legalTypeSelected, completedSteps } =
    legal.drawer;
  const { isSaving } = legal.submit;

  const subtitle =
    currentStep === 0
      ? "Выберите юридический статус контрагента"
      : legalTypeSelected === "selfemployed"
        ? "Данные физического лица — самозанятый"
        : null;

  // Все шаги сохранены — режим свободного редактирования
  const allDone =
    legalTypeSelected === "legal" &&
    completedSteps.has(1) &&
    completedSteps.has(2) &&
    completedSteps.has(3) &&
    completedSteps.has(4);

  // Метка кнопки: при allDone — всегда "Сохранить"
  const isLastStep =
    legalTypeSelected === "selfemployed" ||
    (legalTypeSelected === "legal" && currentStep === 4);
  const nextLabel = allDone ? "Сохранить" : isLastStep ? "Сохранить" : "Далее";

  return (
    <>
      <Drawer.Backdrop
        isOpen={isDrawerOpen}
        onOpenChange={(v) => {
          if (!v) legal.closeDrawer();
        }}
        className="scd-legal-backdrop"
      >
        <Drawer.Content placement="right" className="scd-legal-drawer">
          <Drawer.Dialog className="scd-legal-drawer__dialog">
            <Drawer.CloseTrigger />
            <Drawer.Header className="scd-legal-drawer__header">
              <Drawer.Heading>Юридические данные</Drawer.Heading>
              {subtitle && (
                <p className="scd-legal-drawer__subtitle">{subtitle}</p>
              )}
            </Drawer.Header>

            {legalTypeSelected === "legal" && currentStep >= 1 && (
              <LegalTabNav />
            )}

            <Drawer.Body className="scd-legal-drawer__body">
              {currentStep === 0 && <Step0TypeSelector />}
              {legalTypeSelected === "selfemployed" && currentStep === 1 && (
                <StepSelfEmployed />
              )}
              {legalTypeSelected === "legal" && currentStep === 1 && (
                <Step1Requisites />
              )}
              {legalTypeSelected === "legal" && currentStep === 2 && (
                <Step2Address />
              )}
              {legalTypeSelected === "legal" && currentStep === 3 && (
                <Step3Director />
              )}
              {legalTypeSelected === "legal" && currentStep === 4 && (
                <Step4Bank />
              )}
            </Drawer.Body>

            <Drawer.Footer className="scd-legal-drawer__footer">
              {currentStep > 0 && (
                <Button variant="tertiary" onPress={() => legal.goBack()}>
                  {currentStep === 1 ? "← Тип контрагента" : "Назад"}
                </Button>
              )}
              <div className="scd-legal-drawer__footer-spacer" />
              {currentStep === 0 && (
                <Button
                  variant="primary"
                  isDisabled={!legalTypeSelected}
                  onPress={() => legal.proceedFromTypeSelection()}
                >
                  Продолжить
                </Button>
              )}
              {currentStep > 0 && (
                <Button
                  variant="primary"
                  onPress={() => legal.goNext()}
                  isDisabled={isSaving}
                >
                  {isSaving ? "Сохранение..." : nextLabel}
                </Button>
              )}
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
      <UnsavedStepModal />
    </>
  );
});

LegalDataDrawer.displayName = "LegalDataDrawer";
export default LegalDataDrawer;
